import React, { useEffect, useMemo, useState } from "react";
import SlideOutSidebar from "../../../../../ui-components/slide-out-sidebar/slideOutSidebar";
import Table from "ui-components/table/table";
import { createColumnHelper } from "@tanstack/react-table";
import { useTerminalViewPickupSummaryDetail } from "hooks/usePickupSummary";
import {
    TerminalViewPickupSummaryFilterListModel,
    TerminalViewPickupSummaryQueryParams,
    pickupFilterValueToQueryString,
} from "../../../../../app/data/pickup/models";
import { PickupStatus } from "app/data/common/route";
import "../terminalViewPickup.scss";
import { Routes } from "app/route/RoutesConfig";
import { useHistory } from "react-router-dom";

interface TerminalViewPickupDetailsProps {
    terminalNumber: number;
    terminalName?: string;
    isOpen: boolean;
    onClose: () => void;
    filterData: TerminalViewPickupSummaryFilterListModel;
    data?: any[];
    initialStatus?: string;
    isLoading?: boolean;
}

const columnHelper = createColumnHelper<any>();

const terminalDetailStatusMap: Record<string, PickupStatus> = {
    Created: PickupStatus.CREATED,
    Completed: PickupStatus.COMPLETED,
    Missed: PickupStatus.MISSED,
    Rescheduled: PickupStatus.RESCHEDULE_PICKUP,
    Canceled: PickupStatus.CANCELED,
};

const TerminalViewPickupDetails: React.FC<TerminalViewPickupDetailsProps> = ({
    terminalNumber,
    terminalName,
    isOpen,
    onClose,
    filterData,
    data,
    initialStatus,
    isLoading,
}) => {

    const history = useHistory();

    const tabs = ["Created", "Completed", "Missed", "Rescheduled", "Canceled"];
    const [selectedTab, setSelectedTab] = useState(initialStatus || "Created");

    const mergedFilter = useMemo((): TerminalViewPickupSummaryQueryParams => {
        const isCustom = filterData.timePeriod === "CUSTOM";
        const destinationTerminalNumbers =
            Array.isArray(filterData.destinationTerminalNumbers) && filterData.destinationTerminalNumbers.length > 0
                ? filterData.destinationTerminalNumbers.map((t) => t.value).join(",")
                : undefined;
        const region =
            typeof filterData.region === "string"
                ? filterData.region
                : undefined;

        return {
            currentTerminalCode: filterData.currentTerminalCode,
            timePeriod: filterData.timePeriod,
            region,
            status: terminalDetailStatusMap[selectedTab],
            destinationTerminalNumbers,
            from: isCustom ? pickupFilterValueToQueryString(filterData.from) : undefined,
            to: isCustom ? pickupFilterValueToQueryString(filterData.to) : undefined,
        };
    }, [filterData, selectedTab]);

    const {
        data: terminalDetailListData,
        isLoading: listLoading,
        isFetchingNextPage: listFetchingNextPage,
        fetchNextPage,
        hasNextPage,
    } = useTerminalViewPickupSummaryDetail(terminalNumber, mergedFilter, {
        enabled: isOpen && !!terminalNumber,
    });

    const listRowsFromQuery = useMemo(
        () => terminalDetailListData?.pages.flatMap((p) => p.data?.content ?? []) ?? [],
        [terminalDetailListData]
    );

    const tableData =
        data && Array.isArray(data) && data.length > 0
            ? data
            : listRowsFromQuery;

    const columns = [
        columnHelper.accessor("formattedPickupNumber", {
            id: "formattedPickupNumber",
            header: "Pickup #",
            cell: (cellProps) => (
                <span
                    className="xgs-table-link-cell"
                    onClick={(e) => {
                        e.stopPropagation();
                        history.push(`${Routes.pickup.log}/${cellProps.getValue()}`)
                    }}>
                    {cellProps.getValue()}
                </span >
            ),
            size: 20,
        }),
        columnHelper.accessor("lastEventDate", {
            id: "lastEventDate",
            header: "Last Event Date",
            cell: (cellProps) => <>{cellProps.getValue().toUiDateTimeFormatLocal()}</>,
            size: 50,
        }),
        columnHelper.accessor("requesterCompany", {
            id: "requesterCompany",
            header: "Requester (Company)",
            size: 70,
        }),
        columnHelper.accessor("shipperName", {
            id: "shipperName",
            header: "Shipper",
            cell: (cellProps) => (
                <span>
                    {[
                        cellProps.getValue(),
                        cellProps?.row?.original?.shipperCity,
                        cellProps?.row?.original?.shipperState
                    ]
                        .filter(Boolean)
                        .join(", ")}
                </span>
            ),
            size: 80,
        }),
        columnHelper.accessor("consigneeName", {
            id: "consigneeName",
            header: "Consignee",
            cell: (cellProps) => (
                <span>
                    {[
                        cellProps.getValue(),
                        cellProps?.row?.original?.consigneePostalCode,
                    ]
                        .filter(Boolean)
                        .join(", ")}
                </span>
            ),
            size: 70,
        }),
        columnHelper.accessor("totalUnits", {
            id: "totalUnits",
            header: "Freight Details",
            cell: (cellProps) => (
                <span>
                    Units: {cellProps.getValue()}, lbs: {cellProps?.row?.original?.totalWeight}
                </span>
            ),
            size: 50,
        }),
        columnHelper.accessor("statusNotes", {
            id: "statusNotes",
            header: "Status Notes",
            size: 40,
        }),
    ];

    useEffect(() => {
        if (isOpen && initialStatus) {
            setSelectedTab(initialStatus);
        }
    }, [initialStatus, isOpen]);

    return (
        <SlideOutSidebar
            header={`${terminalName ?? ""} Terminal - Pickups`}
            onClose={onClose}
            show={isOpen}
            size="large"
        >
            <div className="tab-wrapper_terminalview_detail">
                <div className="tab-container">
                    {tabs.map((tab, index) => (
                        <div
                            key={tab}
                            onClick={() => setSelectedTab(tab)}
                            className={`tab-item ${selectedTab === tab ? 'active' : ''} ${index === 0 ? 'first' : index === tabs.length - 1 ? 'last' : ''
                                }`}
                        >
                            {tab}
                        </div>
                    ))}
                </div>
                <div>
                    <Table
                        isLoading={isLoading ?? (listLoading && !tableData.length)}
                        columns={columns}
                        data={tableData}
                        rowHeight={58}
                        minTableHeight={220}
                        noResultsText={`No Pickup Summary ${selectedTab} List`}
                        infiniteScroll
                        infiniteScrollHasNext={!data?.length && !!hasNextPage}
                        infiniteScrollLoading={listFetchingNextPage}
                        onInfiniteScroll={data?.length ? undefined : () => fetchNextPage()}
                        responsive
                    />
                </div>
            </div>
        </SlideOutSidebar>
    );
};

export default TerminalViewPickupDetails;